import { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { isWeb, machineStatus } from '@helpers';
import { TimeCategory } from '@interfaces/base';
import { useCurveLinesContextOptional } from '@v2/pages/common/components/NCFileBatchEvents/context/CurveLinesContext';
import { useAnimatedTableScrollOffsetRef } from '@components/settings/components/common/animatedTable/context/AnimatedTableScrollContext';
import { useEventPercentages } from './useEventPercentages';
import { EventBarChartProps } from '@v2/pages/common/components/NCFileBatchEvents/components/EventBarChart';

export interface UseManageEventBarChartInternalLogicParams
  extends Pick<
    EventBarChartProps,
    | 'eventType'
    | 'eventsPerspective'
    | 'activeTimeInSec'
    | 'changeTimeInSec'
    | 'setupTimeInSec'
    | 'maxTimeInSec'
    | 'idealTimeInSec'
    | 'paidTimeInSec'
    | 'actualTimeInSec'
    | 'scheduledTimeInSec'
    | 'isFirstElement'
  > {}

const useManageEventBarChartInternalLogic = ({
  eventType,
  eventsPerspective,
  activeTimeInSec = 0,
  changeTimeInSec = 0,
  setupTimeInSec = 0,
  maxTimeInSec = 0,
  idealTimeInSec = 0,
  paidTimeInSec = 0,
  actualTimeInSec = 0,
  scheduledTimeInSec = 0,
  isFirstElement = false,
}: UseManageEventBarChartInternalLogicParams) => {
  const { activePercentage, changePercentage, setupPercentage, idealPercentage, paidPercentage, actualPercentage, scheduledPercentage } =
    useEventPercentages({
      eventType,
      eventsPerspective,
      activeTimeInSec,
      changeTimeInSec,
      setupTimeInSec,
      idealTimeInSec,
      paidTimeInSec,
      actualTimeInSec,
      scheduledTimeInSec,
      maxTimeInSec,
    });

  const [barChartWidth, setBarChartWidth] = useState<number>(0);

  const curveLinesContext = useCurveLinesContextOptional();
  const scrollOffsetYRef = useAnimatedTableScrollOffsetRef();

  const shouldRegisterPositions = isFirstElement && curveLinesContext;

  const idealDotRef = useRef<View>(null);
  const paidDotRef = useRef<View>(null);
  const actualDotRef = useRef<View>(null);
  const scheduledDotRef = useRef<View>(null);

  const dotRefs = {
    [TimeCategory.IDEAL]: idealDotRef,
    [TimeCategory.PAID]: paidDotRef,
    [TimeCategory.ACTUAL]: actualDotRef,
    [TimeCategory.SCHEDULED]: scheduledDotRef,
  };

  const dotIndicatorPercentages = {
    [TimeCategory.IDEAL]: idealPercentage,
    [TimeCategory.PAID]: paidPercentage,
    [TimeCategory.ACTUAL]: actualPercentage,
    [TimeCategory.SCHEDULED]: scheduledPercentage,
  };

  const measureMarker = useCallback(
    (type: TimeCategory, ref: React.RefObject<View>) => {
      const scrollOffsetY = scrollOffsetYRef?.current ?? 0;
      if (isWeb) {
        // Web/tablet: use getBoundingClientRect
        const element = ref.current as unknown as HTMLElement;
        if (element?.getBoundingClientRect) {
          const rect = element.getBoundingClientRect();
          const position = {
            x: rect.x + rect.width / 2,
            y: rect.y + rect.height / 2 + scrollOffsetY,
          };
          curveLinesContext?.registerMarkerPosition(type, position);
        }
      } else {
        // Native: use measureInWindow
        ref.current?.measureInWindow((x, y, width, height) => {
          const position = {
            x: x + width / 2,
            y: y + height / 2 + scrollOffsetY,
          };
          curveLinesContext?.registerMarkerPosition(type, position);
        });
      }
    },
    [curveLinesContext, scrollOffsetYRef]
  );

  const measurementVersion = curveLinesContext?.measurementVersion ?? 0;

  // rAF defers measurement to the frame after the React commit so getBoundingClientRect reads the painted layout.
  useEffect(() => {
    if (!shouldRegisterPositions || barChartWidth <= 0) return;
    const rafId = requestAnimationFrame(() => {
      Object.entries(dotIndicatorPercentages).forEach(([category, percentage]) => {
        if (percentage > 0) {
          measureMarker(category as TimeCategory, dotRefs[category as TimeCategory]);
        }
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [shouldRegisterPositions, barChartWidth, idealPercentage, paidPercentage, actualPercentage, scheduledPercentage, measureMarker, measurementVersion]);

  const shouldRender = maxTimeInSec > 0;

  const barSegments = [
    { status: machineStatus.ACTIVE, percentage: activePercentage, hasBorderRadius: !changePercentage && !setupPercentage },
    { status: machineStatus.CHANGE, percentage: changePercentage, hasBorderRadius: !setupPercentage },
    { status: machineStatus.SETUP, percentage: setupPercentage, hasBorderRadius: true },
  ];

  return {
    shouldRender,
    barChartWidth,
    setBarChartWidth,
    barSegments,
    dotIndicatorPercentages,
    dotRefs,
    shouldRegisterPositions,
  };
};

export { useManageEventBarChartInternalLogic };
